import { useState } from 'react';

import { ExternalLink } from '@/components/blocks/links/ExternalLink';
import { InternalLink } from '@/components/blocks/links/InternalLink';

import googleIcon from '@/icons/google.svg';
import yelpIcon from '@/icons/yelp.svg';
import zillowIcon from '@/icons/zillow.svg';

import { reviewLinks, socialLinks } from './data/footer-data';
import { googlePages } from './data/google-data';
import type { ReviewPage } from './data/review-page';
import { yelpPages } from './data/yelp-data';
import { zillowPages } from './data/zillow-data';
import { FooterColumn } from './FooterColumn';
import { ReviewModal } from './ReviewModal';

interface ActiveReviewModal {
  pages: ReviewPage[];
  title: string;
  icon: string;
  iconAlt: string;
}

const reviewModals: Record<string, ActiveReviewModal> = {
  Zillow: {
    pages: zillowPages,
    title: 'Choose a Zillow Page',
    icon: zillowIcon.src,
    iconAlt: 'Zillow',
  },
  Yelp: { pages: yelpPages, title: 'Choose a Yelp Page', icon: yelpIcon.src, iconAlt: 'Yelp' },
  Google: {
    pages: googlePages,
    title: 'Choose a Google Page',
    icon: googleIcon.src,
    iconAlt: 'Google',
  },
};

const linkClass = 'text-footer hover:text-footer-text-hover text-base block leading-[22.4px]';

interface FooterNavProps {
  softwareSupportHref: string;
}

export const FooterNav = ({ softwareSupportHref }: FooterNavProps) => {
  const [activeModal, setActiveModal] = useState<ActiveReviewModal | null>(null);

  return (
    <>
      <div className="flex flex-col md:flex-row md:justify-center text-center md:text-left pt-3 pb-5">
        <FooterColumn title="Company" className="md:px-3.75">
          <InternalLink to="/contact" text="Contact Us" className={linkClass} />
          <InternalLink to="/about" text="About Us" className={linkClass} />
          <InternalLink to="/careers" text="Careers" className={linkClass} />
          <ExternalLink
            href="https://www.markful.com/apparel-841107?CompanyId=604782"
            text="Shop"
            className={linkClass}
          />
        </FooterColumn>

        <FooterColumn title="Affiliates" className="md:pr-3.75">
          <InternalLink to="/preferred-vendors" text="Preferred Vendors" className={linkClass} />
        </FooterColumn>

        <FooterColumn title="Support &amp; Legal" className="md:pl-3.75">
          <InternalLink to="/learn/faq" text="FAQ" className={linkClass} />
          <ExternalLink href={softwareSupportHref} text="Software Support" className={linkClass} />
          <InternalLink to="/privacy-policy" text="Privacy Policy" className={linkClass} />
          <InternalLink
            to="/terms-and-conditions"
            text="Terms and Conditions"
            className={linkClass}
          />
        </FooterColumn>

        <FooterColumn title="Reviews" className="md:pl-8.75 md:pr-3.75">
          {reviewLinks.map(({ text, href, icon }) => {
            const modal = reviewModals[text];

            if (modal) {
              return (
                <button
                  key={text}
                  onClick={() => setActiveModal(modal)}
                  className="flex items-center justify-center md:justify-start gap-1.5 leading-[22.4px] text-footer hover:text-footer-text-hover text-base group/review w-full"
                >
                  <img
                    src={icon}
                    alt={text}
                    className="w-5 h-5 transition-transform group-hover/review:scale-[1.16]"
                  />
                  {text}
                </button>
              );
            }

            return (
              <a
                key={text}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center md:justify-start gap-1.5 leading-[22.4px] text-footer hover:text-footer-text-hover text-base group/review"
              >
                <img
                  src={icon}
                  alt={text}
                  className="w-5 h-5 transition-transform group-hover/review:scale-[1.16]"
                />
                {text}
              </a>
            );
          })}
        </FooterColumn>

        <FooterColumn title="Social" className="md:px-3.75">
          {socialLinks.map(({ text, href, icon, noreferrer }) => (
            <div
              key={text}
              className="flex items-center justify-center md:justify-start gap-1.5 leading-[22.4px] text-base group/social"
            >
              <img
                src={icon}
                alt={text}
                className="w-5 h-5 transition-transform group-hover/social:scale-[1.16]"
              />
              <ExternalLink
                href={href}
                text={text}
                noreferrer={noreferrer}
                className="text-footer hover:text-footer-text-hover"
              />
            </div>
          ))}
        </FooterColumn>
      </div>

      {activeModal && (
        <ReviewModal
          pages={activeModal.pages}
          title={activeModal.title}
          icon={activeModal.icon}
          iconAlt={activeModal.iconAlt}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  );
};
